import { useCallback, useEffect, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, Checkbox, DropZone, InlineStack, Modal, Text, TextField } from "@shopify/polaris";
import supabase from "../../supabase-client";
import ClabeBadge from "../Services/ClabeBadge";
import { getPayoutReceipt, overrideVerification, speiEnabled } from "../../utility/pagos";
import { DOCS_BUCKET, money, rpcError, toCents } from "../../utility/egresos";

// Paying an egreso by SPEI (pagos-svc, payable kind "egreso").
//
// A person with services.pay FREEZES a payment request in SQL
// (request_egreso_payment: amount and what it is for, capped at what is owed
// for a purchase); the usual SPEI dialog then shows what pagos-svc derives
// from that request — amount, holder, CLABE — and sends it. The browser never
// tells pagos-svc an amount or an account. The page shows both steps as one:
// "Pagar por SPEI" opens the amount dialog and goes straight to confirmation.

// The payee's account as the payout path sees it (egreso_payee_account).
export function useEgresoAccount(egresoId) {
  const [account, setAccount] = useState(null);
  const load = useCallback(async () => {
    const { data } = await supabase.rpc("egreso_payee_account", { p_egreso: egresoId });
    setAccount(data?.[0] || null);
  }, [egresoId]);
  useEffect(() => {
    load();
  }, [load]);
  return [account, load];
}

// The open SPEI request of an egreso (at most one) and its latest payout.
export function usePendingSpei(egresoId) {
  const [state, setState] = useState({ request: undefined, payout: null });
  const load = useCallback(async () => {
    const { data } = await supabase
      .from("egreso_payment_requests")
      .select("*")
      .eq("egreso_id", egresoId)
      .eq("status", "pending_payment")
      .maybeSingle();
    let payout = null;
    if (data) {
      const { data: p } = await supabase
        .from("payouts")
        .select("id, status, error, created_at")
        .eq("payable_kind", "egreso")
        .eq("payable_id", String(data.id))
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      payout = p || null;
    }
    setState({ request: data || null, payout });
  }, [egresoId]);
  useEffect(() => {
    load();
  }, [load]);
  const inFlight = !!state.payout && ["queued", "submitted", "awaiting_funds"].includes(state.payout.status);
  return { ...state, inFlight, reload: load };
}

// Todito Pagos & co. (STP, 646…) reject the bank's penny-drop as
// non_existing_account while taking real transfers. Same rule as pagos-svc's
// overridableFailure: a tercero only, approvable with the account statement.
const stpNonExisting = (a) =>
  a?.payee_type === "payee" &&
  a.verification_status === "failed" &&
  /^646\d{15}$/.test(a.clabe || "") &&
  /(^|: )non_existing_account$/.test(a.verification_reason || "");

const accountStatus = (a) =>
  !a?.verification_id
    ? null
    : a.overridden && stpNonExisting(a)
      ? "verified_override"
      : a.verification_status !== "succeeded"
        ? a.verification_status
        : a.overridden && a.match_result !== "verified"
          ? "verified_override"
          : a.match_result;

export function AccountCard({ account, canPay, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  if (!account) return null;
  const status = accountStatus(account);
  const canRetry = account.clabe && (!status || ["failed", "name_mismatch", "rfc_mismatch"].includes(status));
  // The bank answered with someone else's name or RFC. A person with
  // services.pay may accept it in writing (e.g. a provider's reference CLABE
  // that the bank reports under OUR name). Bound to this CLABE.
  const noAccount = status === "failed" && stpNonExisting(account);
  const canApprove = canPay && speiEnabled() && (["name_mismatch", "rfc_mismatch"].includes(status) || noAccount);

  const verify = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("request_bank_verification", {
      p_owner_type: account.payee_type,
      p_owner_id: account.payee_id,
    });
    setBusy(false);
    if (err) return setError(rpcError(err));
    onChanged();
  };

  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm">Cuenta para pagar</Text>
          {account.clabe && <ClabeBadge status={status} holderName={account.holder_reported} />}
        </InlineStack>
        {account.clabe ? (
          <Text tone="subdued">CLABE •••• {account.clabe.slice(-4)}</Text>
        ) : (
          <Text tone="caution">Sin CLABE registrada</Text>
        )}
        {(status === "name_mismatch" || status === "rfc_mismatch") && account.holder_reported && (
          <Text tone="critical" variant="bodySm">El banco reporta: {account.holder_reported}</Text>
        )}
        {noAccount && (
          <Text tone="critical" variant="bodySm">El banco dice que la cuenta no existe.</Text>
        )}
        {error && <Text tone="critical">{error}</Text>}
        {(canRetry || canApprove) && (
          <InlineStack gap="200">
            {canRetry && (
              <Button size="slim" loading={busy} onClick={verify}>
                {status ? "Verificar de nuevo" : "Verificar CLABE"}
              </Button>
            )}
            {canApprove && (
              <Button size="slim" tone="critical" onClick={() => setApproving(true)}>
                Aprobar manualmente
              </Button>
            )}
          </InlineStack>
        )}
      </BlockStack>
      {approving && (
        <ApproveAccountModal
          account={account}
          noAccount={noAccount}
          onClose={() => setApproving(false)}
          onDone={() => {
            setApproving(false);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}

function ApproveAccountModal({ account, noAccount, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      let docPath = null;
      if (noAccount && file) {
        const ext = (file.name.split(".").pop() || "bin").toLowerCase();
        docPath = `payees/${account.payee_id}/clabe_${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from(DOCS_BUCKET).upload(docPath, file, { upsert: false });
        if (upErr) throw upErr;
      }
      await overrideVerification(account.payee_type, account.payee_id, reason.trim(), docPath);
      onDone();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Aprobar cuenta manualmente"
      primaryAction={{
        content: "Aprobar",
        destructive: true,
        onAction: save,
        loading: saving,
        disabled: reason.trim().length < 10,
      }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          {noAccount ? (
            <Banner tone="warning">
              El banco dice que la CLABE •••• {account.clabe?.slice(-4)} no existe. Algunas cuentas de fintechs (Todito
              y otras vía STP) rechazan la verificación de 1 centavo pero sí reciben transferencias. Apruébala solo si ya
              le has pagado a esta CLABE o la tienes de una fuente oficial a nombre de <b>{account.name}</b>. Si el
              primer pago rebota, el dinero regresa.
            </Banner>
          ) : (
            <Banner tone="warning">
              El banco dice que la CLABE •••• {account.clabe?.slice(-4)} es de <b>{account.holder_reported || "otra persona"}</b>,
              no de <b>{account.name}</b>. Apruébala solo si la CLABE viene de una fuente oficial (su factura, su portal,
              su estado de cuenta), nunca de un correo o un mensaje.
            </Banner>
          )}
          {noAccount && (
            <DropZone label="Estado de cuenta o carátula (opcional)" allowMultiple={false} onDrop={(_a, acc) => setFile(acc[0] || null)}>
              {file ? <div style={{ padding: 12 }}><Text>{file.name}</Text></div> : <DropZone.FileUpload />}
            </DropZone>
          )}
          <TextField
            label="¿Por qué es correcta? (queda registrado)"
            requiredIndicator
            value={reason}
            onChange={setReason}
            multiline={2}
            autoComplete="off"
            helpText="Mínimo 10 caracteres"
          />
          <Text tone="subdued" variant="bodySm">
            La aprobación vale solo para esta CLABE. Si la cuenta cambia, se vuelve a verificar.
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// Shown only while a SPEI request is open: what it is and where it stands.
export function PendingSpeiCard({ request, payout, inFlight, onCancelled }) {
  const [error, setError] = useState(null);
  if (!request) return null;
  const cancel = async () => {
    setError(null);
    const { error: err } = await supabase.rpc("cancel_egreso_payment_request", { p_request: request.id });
    if (err) return setError(rpcError(err));
    onCancelled();
  };
  const badge = {
    awaiting_funds: <Badge tone="attention">Esperando fondos</Badge>,
    queued: <Badge tone="info">En cola</Badge>,
    submitted: <Badge tone="info">Enviando</Badge>,
    failed: <Badge tone="critical">Falló</Badge>,
  }[payout?.status];
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text>
            SPEI {inFlight ? "en curso" : "por enviar"}: <b>{money(request.amount_cents)}</b>
            {request.description ? ` · ${request.description}` : ""}
          </Text>
          <InlineStack gap="200" blockAlign="center">
            {badge}
            {!inFlight && (
              <Button variant="plain" tone="critical" onClick={cancel}>Cancelar</Button>
            )}
          </InlineStack>
        </InlineStack>
        {request.returned_at && <Banner tone="warning">El banco devolvió el SPEI anterior: {request.returned_reason}</Banner>}
        {payout?.status === "failed" && payout.error && <Text tone="critical" variant="bodySm">{payout.error}</Text>}
        {error && <Text tone="critical">{error}</Text>}
      </BlockStack>
    </Card>
  );
}

// Amount + concept of a new SPEI; on success the caller opens the SPEI dialog.
export function SpeiRequestModal({ egreso, balance, nextPlanned, onClose, onCreated }) {
  // No agreed amount and no invoice yet: money advanced before any document.
  const isAnticipo = !(egreso.committed_cents > 0) && !((balance?.cost_cents || 0) > 0);
  const suggested = nextPlanned
    ? nextPlanned.amount_cents - nextPlanned.covered
    : isAnticipo
      ? 0
      : balance?.por_pagar_cents || 0;
  const [kind, setKind] = useState("paid_out");
  const [amount, setAmount] = useState(suggested > 0 ? (suggested / 100).toFixed(2) : "");
  const [description, setDescription] = useState(nextPlanned?.label || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const cents = toCents(amount);
  const cap = kind === "deposit_out" || isAnticipo ? null : balance?.por_pagar_cents || 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    const { data, error: err } = await supabase.rpc("request_egreso_payment", {
      p_egreso: egreso.id,
      p_kind: kind,
      p_amount_cents: cents,
      p_description: description,
    });
    setSaving(false);
    if (err) return setError(rpcError(err));
    onCreated(data);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Pagar por SPEI"
      primaryAction={{ content: "Continuar", onAction: save, loading: saving, disabled: !(cents > 0) || (cap != null && cents > cap) }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}
          <Checkbox
            label="Es depósito en garantía (debe regresar)"
            checked={kind === "deposit_out"}
            onChange={(v) => setKind(v ? "deposit_out" : "paid_out")}
          />
          <TextField
            label="Monto"
            requiredIndicator
            prefix="$"
            type="number"
            value={amount}
            onChange={setAmount}
            autoComplete="off"
            helpText={cap != null ? `Por pagar: ${money(cap)}` : undefined}
            error={cap != null && cents > cap ? `No puede ser más de ${money(cap)}` : undefined}
          />
          <TextField
            label="Nota interna"
            value={description}
            onChange={setDescription}
            autoComplete="off"
            placeholder="Anticipo 50%, resto, renta semana 41…"
            helpText="Solo para GoMove. El concepto SPEI (folio de la factura) se propone en el siguiente paso."
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// A paid SPEI: Fintoc's comprobante, minted on click (signed URL, short-lived).
export async function openSpeiReceipt(payoutId) {
  const { download_url } = await getPayoutReceipt(payoutId);
  window.open(download_url, "_blank", "noopener");
}
